import { CareerPath } from '@backrs_ui/common-ui';

import {
  CareerPathMeaning,
  CareerPathEnjoyment,
  CareerUserResponseSet,
  CareerQuestion,
} from '@backrs_ui/grow-ui/src/models/models';

import { applyCaregiverFlexibilityAdjustment } from './caregiverFlexibility';
import { ScoreCalculationResult } from '../calculateSurveyResults';
import {
  OPTION_ID_TO_DISLIKE_KEY_MAP,
  OPTION_ID_TO_ENJOYMENT_KEY_MAP,
  OPTION_ID_TO_MEANING_KEY_MAP,
  USER_SUMS_QUESTION_KEYS,
} from '../questionConstants';
import { getQuestionByKey, getUserResponse } from '../scoreHelpers';

const ENJOYMENT_POSITIVE_FACTOR = 2;
const DISLIKE_NEGATIVE_FACTOR = -3;
const MAX_VALUE = 71;

type CalculateSumsParams = Readonly<{
  responseSet: CareerUserResponseSet;
  questions: CareerQuestion[];
  careerPath: CareerPath;
  careerPathMeanings: CareerPathMeaning[];
  careerPathEnjoyments: CareerPathEnjoyment[];
  careerResults: ScoreCalculationResult;
}>;

export const calculateSums = ({
  responseSet,
  questions,
  careerPath,
  careerPathMeanings,
  careerPathEnjoyments,
}: CalculateSumsParams): number => {
  const careerPathMeaning = careerPathMeanings.find(
    (meaning) => meaning.career_path_id === careerPath.id,
  );

  const careerPathEnjoyment = careerPathEnjoyments.find(
    (enjoyment) => enjoyment.career_path_id === careerPath.id,
  );

  if (!careerPathMeaning || !careerPathEnjoyment) {
    return 0;
  }

  const meaningSum = calcCareerPathScore(
    responseSet,
    questions,
    USER_SUMS_QUESTION_KEYS.SOURCES_OF_MEANING_QUESTION,
    OPTION_ID_TO_MEANING_KEY_MAP,
    careerPathMeaning,
    1,
  );

  const enjoymentSum = calcCareerPathScore(
    responseSet,
    questions,
    USER_SUMS_QUESTION_KEYS.ACTIVITIES_YOU_ENJOY_QUESTION,
    OPTION_ID_TO_ENJOYMENT_KEY_MAP,
    careerPathEnjoyment,
    ENJOYMENT_POSITIVE_FACTOR,
  );

  const dislikeSum = calcCareerPathScore(
    responseSet,
    questions,
    USER_SUMS_QUESTION_KEYS.ACTIVITIES_YOU_DONT_LIKE_QUESTION,
    OPTION_ID_TO_DISLIKE_KEY_MAP,
    careerPathEnjoyment,
    DISLIKE_NEGATIVE_FACTOR,
  );

  // the Google Sheets formula then has us take the total sum, divide by the max value, and multiply by 100
  const result = ((meaningSum + enjoymentSum + dislikeSum) / MAX_VALUE) * 100;

  // Apply caregiver flexibility adjustment
  const adjustedResult = applyCaregiverFlexibilityAdjustment(
    result,
    responseSet,
    questions,
    careerPath,
  );

  return adjustedResult;
};

const calcCareerPathScore = (
  responseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
  questionKey: string,
  optionIdToKeyMap: Record<string, string>,
  careerPathValueObject: Record<string, number>,
  factor = 1,
): number => {
  const question = getQuestionByKey(questions, questionKey);

  if (!question) {
    return 0;
  }

  const response = getUserResponse(responseSet, question.id);

  if (!response) {
    return 0;
  }

  // Create a map from option IDs to option keys for the current question by looking up the calculator_key
  const optionIdToOptionKeyMap: Record<number, string> = question.options.reduce(
    (map, option) => {
      if (option.id && option.calculator_key) {
        map[option.id] = option.calculator_key;
      }

      return map;
    },
    {} as Record<number, string>,
  );

  const totalScore = response.career_question_option_ids
    .map((optionId) => {
      const optionKey = optionIdToOptionKeyMap[optionId];
      const valueKey = optionIdToKeyMap[optionKey];

      return valueKey ? careerPathValueObject[valueKey] : 0;
    })
    .reduce((sum, value) => sum + value * factor, 0);

  return totalScore;
};
